<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    default: null,
  },
});

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const enabled = computed(() =>
  Boolean(props.conversationId && accountId.value)
);

// Server-reported state.
const phase = ref('idle');
const statusTs = ref(0); // wall-clock ts of the latest n8n status emit
const fireBusy = ref(false);

// Local UI intent latch. The poll mirrors n8n's flow:status, which keeps
// advancing — and being rewritten — even AFTER the agent clicks Cancel, and can
// briefly read a terminal phase right after Assist Now before n8n registers the
// flow. Without a latch the button morphs Assist Now <-> Cancel on every such
// poll ("flashing"), and Cancel looks broken because the badge snaps straight
// back. The latch holds the agent's intended state until the server agrees.
const intent = ref(null); // 'assist' | 'cancel' | null
const intentTs = ref(0);
// Cancel holds 'idle' until the cancelled run actually drains (so the badge
// doesn't reappear mid-run); assist holds 'queued' only until n8n registers a
// live phase. Both have a safety cap so a stuck flow can't freeze the control.
const CANCEL_HOLD_MS = 30000;
const ASSIST_HOLD_MS = 8000;
const TERMINAL_PHASES = ['idle', 'done', 'escalated', 'error'];
const isTerminal = p => !p || TERMINAL_PHASES.includes(p);
// A cancelled run is only "over" on a real end-of-run phase — NOT on 'idle'. The
// cancel endpoint itself resets flow:status to 'idle', and n8n then writes one
// more phase (drafting/sending/done) as it finishes; treating 'idle' as the end
// would release the latch too early and let that trailing write flash the badge
// back. So hold the cancel latch until done/escalated/error (or the time cap).
const RUN_ENDED_PHASES = ['done', 'escalated', 'error'];
// Phases where the AI flow is actively running (drives the status badge + the
// faster poll cadence). Single source of truth, reused by isProcessing below.
const PROCESSING_PHASES = [
  'queued',
  'recall',
  'triaging',
  'reviewing',
  'drafting',
  'sending',
];

// Client clock — drives the live debounce countdown without polling.
const nowTs = ref(Date.now());

// Debounce wait is fixed at 15s in the n8n "Debounce Wait" node (workflow
// rAEMt5eehN0Cno7t, amount:15) — drives the live countdown.
const DEBOUNCE_MS = 15000;

let pollTimer = null;
let nowTicker = null;

const baseUrl = () =>
  `/api/v1/accounts/${accountId.value}/mh_ai_assist/${props.conversationId}`;

const fetchStatus = async () => {
  if (!enabled.value) return;
  let serverPhase = 'idle';
  let serverTs = 0;
  try {
    const res = await window.axios.get(`${baseUrl()}/status`);
    const data = res.data || {};
    serverPhase = data.phase || 'idle';
    serverTs = data.statusTs || 0;
  } catch (_err) {
    // Silent — keep last-known state. Don't noisily fail in CS UI just
    // because mk-backend hiccupped.
    return;
  }
  applyServerStatus(serverPhase, serverTs);
};

// Reconcile a freshly-polled server phase against any pending local intent so
// the control stays stable instead of flickering while n8n's status churns.
const applyServerStatus = (serverPhase, serverTs) => {
  if (intent.value === 'cancel') {
    const expired = Date.now() - intentTs.value > CANCEL_HOLD_MS;
    // Release the latch when the cancelled run truly ends (done/escalated/error),
    // when a brand-NEW flow begins (a 'debouncing' with a newer ts than the
    // cancel — a fresh inbound worth showing; NOT the same debounce we just
    // cancelled), or on the safety cap. Otherwise hold 'idle': ignore the
    // trailing processing writes and the endpoint's own 'idle' reset so the
    // badge can't flash back.
    const freshRun =
      serverPhase === 'debouncing' && serverTs > intentTs.value;
    if (RUN_ENDED_PHASES.includes(serverPhase) || freshRun || expired) {
      intent.value = null;
      phase.value = serverPhase;
      statusTs.value = serverTs;
    }
    return;
  }

  if (intent.value === 'assist') {
    const expired = Date.now() - intentTs.value > ASSIST_HOLD_MS;
    // Hold the optimistic 'queued' until n8n registers a live (non-terminal)
    // phase, so we don't blink back to Assist Now before the flow shows up.
    if (!isTerminal(serverPhase) || expired) {
      intent.value = null;
      phase.value = serverPhase;
      statusTs.value = serverTs;
    }
    return;
  }

  phase.value = serverPhase;
  statusTs.value = serverTs;
};

// Live countdown derived from statusTs while phase=debouncing. Updates every
// 100ms via nowTicker so the digit transitions feel smooth between polls.
const remainingMs = computed(() => {
  if (phase.value !== 'debouncing' || !statusTs.value) return 0;
  return Math.max(0, DEBOUNCE_MS - (nowTs.value - statusTs.value));
});

// Poll fast only while something is actually happening. Back off hard when
// idle/terminal so a conversation left open (often a background tab open all
// day) stops hitting mk-backend every 1.5s. Idle polling only needs to notice
// an externally triggered flow starting; up to ~5s latency there is fine since
// the debounce is 15s and its countdown is interpolated client-side.
const pollInterval = computed(() => {
  if (phase.value === 'debouncing' || phase.value === 'queued') return 500;
  if (PROCESSING_PHASES.includes(phase.value)) return 1500;
  return 5000;
});

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const schedulePoll = () => {
  stopPolling();
  pollTimer = setTimeout(async () => {
    // Skip the network call while the tab is backgrounded — a hidden tab needs
    // no live status and shouldn't keep hitting mk-backend. The timer keeps
    // cycling; onVisible does an immediate refresh when the agent returns.
    if (!document.hidden) await fetchStatus();
    schedulePoll();
  }, pollInterval.value);
};

const assistNow = async () => {
  if (!enabled.value || fireBusy.value) return;
  fireBusy.value = true;
  // Optimistic + latched: show 'queued' (badge + Cancel) immediately and hold it
  // so the next polls don't bounce us back to Assist Now before n8n registers
  // the flow. The latch clears as soon as the server reports a live phase.
  intent.value = 'assist';
  intentTs.value = Date.now();
  phase.value = 'queued';
  statusTs.value = Date.now();
  try {
    await window.axios.post(`${baseUrl()}/assist_now`);
  } catch (_err) {
    // Firing failed — drop the latch so the UI reverts on the next poll.
    intent.value = null;
    phase.value = 'idle';
  } finally {
    fireBusy.value = false;
  }
};

// Cancel — only offered while the AI is actively running (isProcessing). Sets a
// server-side suppress flag (n8n's Send Gate halts this turn's output) and
// clears the phase so the badge + button revert to Assist Now at once.
const cancelAi = async () => {
  if (!enabled.value) return;
  // Latch 'cancel' so the badge + Cancel button don't snap back when n8n writes
  // the next flow:status before the run drains. UI returns to Assist Now at once
  // and stays there; the server-side suppress flag is the authoritative stop.
  intent.value = 'cancel';
  intentTs.value = Date.now();
  phase.value = 'idle';
  try {
    await fetch('https://sapi.mkhb.my/api/chat-triage/cancel-ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId: props.conversationId }),
    });
  } catch (_err) {
    // best-effort; the server-side suppress flag is the authoritative stop
  }
};

watch(
  () => props.conversationId,
  () => {
    intent.value = null; // a different conversation = a clean slate
    phase.value = 'idle';
    statusTs.value = 0;
    if (enabled.value) {
      fetchStatus();
      schedulePoll();
    }
  }
);

// Resume immediately when the tab is refocused so the agent sees fresh status
// without waiting out an interval (polling pauses while hidden — see schedulePoll).
const onVisible = () => {
  if (!document.hidden && enabled.value) {
    fetchStatus();
    schedulePoll();
  }
};

onMounted(() => {
  if (enabled.value) {
    fetchStatus();
    schedulePoll();
    nowTicker = setInterval(() => {
      nowTs.value = Date.now();
    }, 100);
  }
  document.addEventListener('visibilitychange', onVisible);
});

onBeforeUnmount(() => {
  stopPolling();
  if (nowTicker) clearInterval(nowTicker);
  document.removeEventListener('visibilitychange', onVisible);
});

// ─── Display logic ──────────────────────────────────────────────────────
// Per-phase labels for the morphing status badge. The badge only shows during
// active processing — once done/escalated/error/idle, the badge hides and only
// the Assist Now button remains.
const PHASE_LABELS = {
  queued: 'Starting…',
  recall: 'Recalling history…',
  triaging: 'AI thinking…',
  reviewing: 'Manager reviewing…',
  drafting: 'Drafting…',
  sending: 'Sending…',
};

const isProcessing = computed(() => PROCESSING_PHASES.includes(phase.value));

// Countdown mode: showing the debounce timer in the SAME badge slot.
const showCountdown = computed(
  () => phase.value === 'debouncing' && remainingMs.value > 0
);

// Processing mode: showing a per-phase label in the same badge slot.
const showProcessing = computed(
  () => !showCountdown.value && isProcessing.value
);

// Badge as a whole hides on done/escalated/error/idle.
const showBadge = computed(() => showCountdown.value || showProcessing.value);

const countdownLabel = computed(
  () => `${Math.ceil(remainingMs.value / 1000)}s`
);
const processingLabel = computed(
  () => PHASE_LABELS[phase.value] || 'AI working…'
);

const assistTooltip = 'Run AI on the latest customer message';

// Cancel is offered the WHOLE time the AI is engaged — including the debounce
// countdown, where cancelling aborts the run before any LLM tokens are spent
// (n8n checks the flag right after the wait). Outside an active flow the button
// reverts to Assist Now.
const isCancellable = computed(
  () => isProcessing.value || phase.value === 'debouncing'
);
const cancelTooltip = computed(() =>
  showCountdown.value
    ? 'Cancel — stops the run before the AI starts (no tokens used)'
    : "Stop the AI — it won't post a draft or reply for this message"
);
</script>

<template>
  <div v-if="enabled" class="flex items-center gap-1.5">
    <!-- Single morphing badge: countdown during debounce, phase label while
         AI is working. Hides entirely once done/escalated/error/idle so the
         CS toolbar isn't cluttered with stale status. -->
    <div
      v-if="showBadge"
      class="flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium tabular-nums transition-colors"
      :class="
        showCountdown
          ? 'font-mono text-n-slate-12 bg-n-slate-3 border border-n-slate-5'
          : 'text-n-violet-9 bg-n-violet-3'
      "
      :title="
        showCountdown
          ? `AI debounce: ${countdownLabel} until flow runs`
          : `AI: ${processingLabel}`
      "
    >
      <span v-if="showCountdown" class="i-lucide-timer text-n-violet-9" />
      <span v-else class="i-lucide-loader-2 animate-spin" />
      <span>{{ showCountdown ? countdownLabel : processingLabel }}</span>
    </div>

    <!-- Right action shares one slot: Cancel the whole time the AI is engaged
         (debounce countdown + processing), else Assist Now (idle/done = run). -->
    <NextButton
      v-if="isCancellable"
      ghost
      sm
      class="text-n-ruby-9 hover:enabled:!bg-n-ruby-3 !h-7"
      label="Cancel"
      icon="i-lucide-x"
      :title="cancelTooltip"
      @click="cancelAi"
    />
    <NextButton
      v-else
      ghost
      sm
      :disabled="fireBusy"
      class="text-n-violet-9 hover:enabled:!bg-n-violet-3 !h-7"
      :label="fireBusy ? 'Firing…' : 'Assist Now'"
      icon="i-lucide-zap"
      :title="assistTooltip"
      @click="assistNow"
    />
  </div>
</template>
